import { call, put, select, takeLatest } from 'redux-saga/effects'
import { ZipService } from '../services/zip.services'
import Logger from '../../../services/logger.service'
import { CityStateLookupRequest, IZipState, ZipResponse } from '../store/zip.types'
import { ZipConstants } from '../store/zip.constants'
import { getZipSelector } from '../selectors/zip.selector'
import { getPatientDemographicsOptionsSelector } from '../../../features/options/selectors'
import { setAlertMessage, setZipResponse } from '../store/zip.actions'
import { handleApiError } from '../../apiErrorHandler/store/apiErrorHandler.actions'
import { getAuthenticationJwtSelector } from '../../../features/login/selectors/getAuthenticationJwt.selector'
import { setAuthenticationJwt } from '../../../features/login/store/login.actions'
import { isOffline } from '../../../utilities'

export function* getCityState() {
  if (isOffline()) {
    return
  }

  try {
    const zip: IZipState = yield select(getZipSelector)
    const { zipCode, cityAction, stateAction } = zip
    const { stateOptions } = yield select(getPatientDemographicsOptionsSelector)
    const authenticationJwt = yield select(getAuthenticationJwtSelector)

    if (!zipCode) {
      throw Error('ZIP Code does not exist')
    }

    const cityStateLookupRequest: CityStateLookupRequest = {
      zipCode: zipCode,
    }

    const zipService = new ZipService(authenticationJwt as string)
    const response: ZipResponse = yield call([zipService, zipService.doCityStateLookup], cityStateLookupRequest)

    response.success = false
    response.failure = false

    Logger.info(`ZipResponse status: ${response.status}`)

    if (response.status === 200) {
      yield put(setAuthenticationJwt(response.authenticationJwt))
      if (response.data && response.data.cityStateLookup) {
        response.success = true

        yield put({ type: cityAction, city: { name: response.data.cityStateLookup.city, nemsisCode: '' } })
        yield put({
          type: stateAction,
          state: stateOptions.find((option: any) => option.id === response.data?.cityStateLookup.state),
        })
      } else {
        response.failure = true
        response.responseMessage = 'City and State cannot be determined based on the Zip code value entered.'
        yield put(setAlertMessage(response.responseMessage))
      }
    } else {
      response.failure = true
      yield put(handleApiError(response))
    }
    const subsetOfResponse = (({ success, failure, responseMessage }) => ({
      success,
      failure,
      responseMessage,
    }))(response)
    yield put(setZipResponse(subsetOfResponse))
  } catch (e: any) {
    const cause = e.message ? e.message : 'unknown'
    const response = ZipService.createErrorResponse(`An error occurred during CityStateLookup. Cause: ${cause}`)
    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: `An error occurred during CityStateLookup. Cause: ${e}`,
        },
      ],
    }
    Logger.error(`An error occurred during CityStateLookup. Cause: ${cause}`)
    yield put(setZipResponse(response))
    yield put(handleApiError(errorResponse))
  }
}

export function* getCityStateNoCall() {
  return
}

export function* getCityStateSaga() {
  yield takeLatest([ZipConstants.SET_ZIP_STATE], getCityStateNoCall)
}
